import React from "react";
import { extendObservable, action, runInAction } from "mobx";
import { post } from "../lib/http";
import { fakeProgressHack } from "../utils/fakeProgressHack";
import { trackAction } from "../tracking";
import OnboardingActions from "../tracking/actions";

export class ResumeStore {
  constructor() {
    extendObservable(
      this,
      {
        step: 0,
        linkedInProfileUrl: "",
        // hacky change tracker - may need to rethink profile url input UX
        // because we save on change, don't want to show the end state as the
        // user may still be typing/chaning the url
        linkedInProfileUrlChangedByUser: false,
        linkedInToken: "",
        hasError: false,
        isLoading: false,
        isBusy: false,
        showModal: false,
        showConfirmationStatusBar: false,
        fileprogress: 0,
        uploadProcessType: null,
        
        
        resumeReceivedUTC: "",
        resumeFileName: "",
        resumeURL: "",
        resumeURLCreateDateUTC: "",
        resumeDateUTC: null,
        resumeIsStale: false,
      },
      null,
      { deep: true }
    );
  }

  init = action(userData => {
    const copy = { ...userData };
    // user has both resume upload & resume url saved
    // take the latest one as the current one
    if (!!copy.resumeURLCreateDateUTC && !!copy.resumeReceivedUTC) {
      if (
        new Date(copy.resumeURLCreateDateUTC) > new Date(copy.resumeReceivedUTC)
      ) {
        copy.resumeReceivedUTC = "";
        copy.resumeFileName = "";
        this.resumeDateUTC = new Date(copy.resumeURLCreateDateUTC);
      } else {
        copy.resumeURLCreateDateUTC = "";
        copy.resumeURL = "";
        this.resumeDateUTC = new Date(copy.resumeReceivedUTC);
      }
    }

    // copy data to store
    this.resumeURL = copy.resumeURL || "";
    this.resumeURLCreateDateUTC = copy.resumeURLCreateDateUTC || "";
    this.resumeReceivedUTC = copy.resumeReceivedUTC || "";
    this.resumeFileName = copy.resumeFileName || "";
    this.resumeIsStale = !!this.resumeDateUTC && this.resumeDateUTC < new Date(new Date().setMonth(new Date().getMonth() - 6));
    this.linkedInProfileUrl = copy.linkedInProfileUrl;
  });

  uploadResume = action(file => {
    if (!file) {
      this.hasError = { statusText: "Please select a file" };
      return;
    }
    trackAction(OnboardingActions.UploadStarted, { fileName: file.name });
    this.uploadProcessType = "file";
    this.hasError = false;
    this.isLoading = true;
    this.isBusy = true;

    const formData = new FormData();
    formData.append("resume", file);
    formData.append("fileName", file.name);

    const [fakeProgressPromise] = fakeProgressHack(
      value => runInAction(() => (this.fileprogress = value)),
      2000
    );
    return Promise.all([
      post("api/uploadResume", formData, {}),
      fakeProgressPromise
    ])
      .then(() =>
        runInAction(() => {
          trackAction(OnboardingActions.UploadCompleted);
          this.uploadProcessType = null;
          this.showConfirmationStatusBar = true;
          this.resumeFileName = file.name;
          this.isLoading = false;
          this.isBusy = false;
          this.hasError = false;
          this.resumeReceivedUTC = new Date().toISOString();
        })
      )
      .catch(err =>
        runInAction(() => {
          this.isLoading = false;
          this.isBusy = false;
          trackAction(OnboardingActions.UploadError, this.hasError);
          return Promise.reject(err)
        })
      );
  });


  saveLinkedInURL = action((profileURL) => {
    this.isLoading = false;
    this.isBusy = true;
    this.uploadProcessType = "url";
    return post("api/saveProfileURL", { profileURL })
      .then(r =>
        runInAction(() => {
          if (!!r && r.statusText === "success") {
            this.isLoading = false;
            this.isBusy = false;
            this.showConfirmationStatusBar = true;
            this.linkedInProfileUrl = profileURL;
            this.linkedInProfileUrlChangedByUser = true;
            this.uploadProcessType = null;
            trackAction(OnboardingActions.SAVELINKEDINURL);
          }else{
            throw new Error("Unknown error saving linkedin profile url")
          }
        })
      )
      .catch(err => {
        runInAction(() => {
          this.isLoading = false;
          this.isBusy = false;
          this.hasError = {
            status: err.status,
            statusText: err.statusText
          };
          trackAction(OnboardingActions.LINKEDINURLERROR, this.hasError);
          throw new Error(err)
        });
      });
  });

  saveResumeURL = action(resumeURL => {
    this.isLoading = true;
    this.isBusy = true;
    this.uploadProcessType = "url";
    const [fakeProgressPromise] = fakeProgressHack(
      value => runInAction(() => (this.fileprogress = value)),
      1500
    );
    return Promise.all([
      post("api/saveResumeURL", { resumeURL }),
      fakeProgressPromise
    ])
      .then(r =>
        runInAction(() => {
          if (!!r && r[0].statusText === "success") {
            this.resumeURL = resumeURL;
            this.resumeURLCreateDateUTC = new Date().toISOString();
            this.isLoading = false;
            this.isBusy = false;
            this.showConfirmationStatusBar = true;
            trackAction(OnboardingActions.ResumeURLAdded);
            this.uploadProcessType = null;
          }
        })
      )
      .catch(err => {
        runInAction(() => {
          this.isLoading = false;
          this.isBusy = false;
          this.hasError = {
            status: err.status,
            statusText: err.statusText
          };
          trackAction(OnboardingActions.ResumeURLError, this.hasError);
          throw new Error(err)
        });
      });
  });

  RESET = action(() => {
    this.hasError = false;
    this.isLoading = false;
    this.isBusy = false;
    this.fileprogress = 0;
    this.resumeURL = "";
    this.resumeFileName = "";
    this.resumeReceivedUTC = null
    this.showConfirmationStatusBar = false;
    trackAction(OnboardingActions.ResetResumeView);
  });
}

export const resumeStore = new ResumeStore();
export const ResumeStoreContext = React.createContext();
